import { useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { getStripe, stripeConfigured } from '@/lib/stripe';
import { MoneyLedger } from '@/components/MoneyLedger';
import { api } from '@/lib/api';
import { useCart } from '@/store/cart';
import { DeliveryAddressBlock } from '@/components/order/DeliveryAddressBlock';

interface Breakdown { itemGBP: number; serviceGBP: number; deliveryGBP: number; creditGBP?: number; vatGBP: number; totalGBP: number; member?: boolean }
interface CheckoutState {
  clientSecret?: string;
  amountGBP?: number;
  title?: string;
  reference?: string;
  cart?: boolean;
  productId?: string;
  breakdown?: Breakdown;
  demo?: boolean;
  paymentRef?: string;
  guest?: boolean;
  guestToken?: string;
}

/**
 * Escrow checkout. The order + PaymentIntent were created by POST /orders on the
 * product page; we receive the clientSecret via router state and collect payment
 * with Stripe's Payment Element. On success the buyer's funds are captured into
 * the Souqli platform balance (held until you confirm receipt) and the order moves to AWAITING_DROP
 * via the payment_intent.succeeded webhook.
 */
export default function Checkout() {
  const { orderId } = useParams();
  const { state } = useLocation() as { state: CheckoutState | null };
  const clientSecret = state?.clientSecret;
  const stripePromise = getStripe();

  // Demo mode: backend created the order without a live PaymentIntent (no Stripe
  // keys). Simulate the payment so the order still completes end-to-end.
  if (state?.demo && state?.paymentRef) {
    return (
      <DemoPayment
        orderId={orderId}
        paymentRef={state.paymentRef}
        amountGBP={state.amountGBP}
        title={state.title}
        reference={state.reference}
        isCart={!!state.cart}
        productId={state.productId}
        breakdown={state.breakdown}
        guest={!!state.guest}
      />
    );
  }

  // Guard rails: missing config or a direct visit without an in-flight order.
  if (!stripeConfigured || !stripePromise) {
    return (
      <Shell title="Payments not configured">
        <p className="text-mute text-sm">
          Stripe isn’t set up yet. Add your <span className="mono">VITE_STRIPE_PUBLIC_KEY</span> to
          <span className="mono"> frontend/.env</span> (and the secret key on the backend) to enable checkout.
        </p>
        <Link to="/" className="btn btn-outline mt-6">Back to shopping</Link>
      </Shell>
    );
  }

  if (!clientSecret) {
    return (
      <Shell title="Nothing to pay for">
        <p className="text-mute text-sm">This checkout link has expired or was opened directly. Start from a product to begin a secure order.</p>
        <Link to="/c/all" className="btn btn-outline mt-6">Browse items</Link>
      </Shell>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'flat',
          variables: { colorPrimary: '#D98A2B', colorBackground: '#FFFFFF', borderRadius: '10px', fontFamily: 'Inter, sans-serif' },
        },
      }}
    >
      <PaymentForm orderId={orderId} amountGBP={state?.amountGBP} title={state?.title} reference={state?.reference} isCart={!!state?.cart} productId={state?.productId} breakdown={state?.breakdown} guest={!!state?.guest} />
    </Elements>
  );
}

function PaymentForm({ orderId, productId, amountGBP, title, reference, isCart, breakdown, guest }: { orderId?: string; productId?: string; amountGBP?: number; title?: string; reference?: string; isCart?: boolean; breakdown?: Breakdown; guest?: boolean }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const clearCart = useCart((s) => s.clear);
  const remove = useCart((s) => s.remove);
  const [submitting, setSubmitting] = useState(false);

  async function pay(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard/orders${isCart ? '?cartpaid=1' : ''}` },
      redirect: 'if_required',
    });

    if (error) {
      toast.error(error.message ?? 'Payment could not be completed.');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      toast.success('Payment secured - your money is held safely until you collect.');
      // Self-heal: settle immediately instead of waiting on the webhook, so the
      // order never sits stuck in "Pending" if the webhook is missed or slow.
      try { await api.post('/orders/confirm', { paymentRef: paymentIntent.id }); } catch { /* webhook is the backup */ }
      if (isCart) clearCart(); else if (productId) remove(productId);
      navigate(`/dashboard/orders${orderId && orderId !== 'cart' ? `?focus=${orderId}` : ''}`);
      return;
    }
    setSubmitting(false);
  }

  return (
    <Shell title="Secure checkout">
      <div className="grid md:grid-cols-[1fr_300px] gap-8 items-start">
        <form onSubmit={pay} className="space-y-5">
          <PaymentElement options={{ layout: 'tabs' }} />
          <button type="submit" disabled={!stripe || submitting} className="btn btn-lg btn-primary w-full">
            {submitting ? 'Processing…' : `Pay${amountGBP ? ` £${amountGBP.toFixed(2)}` : ''} securely`}
          </button>
          <p className="stamp text-mute text-[9px] text-center">
            SECURE · PROCESSED BY STRIPE · FUNDS HELD SAFELY UNTIL YOU CONFIRM COLLECTION
          </p>
        </form>

        <div className="space-y-4">
        <aside className="card p-5">
          <div className="stamp text-gold-dark text-[9px]">ORDER SUMMARY</div>
          {title && <div className="display text-lg mt-2 leading-tight">{title}</div>}
          {reference && <div className="stamp text-mute text-[9px] mt-1">REF {reference}</div>}
          <div className="border-t border-warmline mt-4 pt-4 space-y-2 text-sm">
            {breakdown ? (
              <>
                <Row k="Item(s)" v={`£${breakdown.itemGBP.toFixed(2)}`} />
                {breakdown.member ? (
                  <Row k={breakdown.serviceGBP === 0 ? 'Buyer protection (Member · included)' : 'Buyer protection (Member · 3.5% + 60p)'} v={`£${breakdown.serviceGBP.toFixed(2)}`} />
                ) : (
                  <Row k="Buyer protection fee (4.5% + 70p)" v={`£${breakdown.serviceGBP.toFixed(2)}`} />
                )}
                <Row k="Delivery" v={`£${(breakdown.deliveryGBP + (breakdown.creditGBP ?? 0)).toFixed(2)}`} />
                {breakdown.creditGBP ? <Row k="£4 delivery voucher" v={`−£${breakdown.creditGBP.toFixed(2)}`} /> : null}
              </>
            ) : (
              <>
                <Row k="Item" v={amountGBP ? `£${amountGBP.toFixed(2)}` : '-'} />
                <Row k="Buyer protection" v="Included" />
              </>
            )}
          </div>
          <div className="border-t border-warmline mt-4 pt-4 flex items-baseline justify-between">
            <span className="text-sm text-mute">Total</span>
            <span className="display text-2xl">£{(breakdown?.totalGBP ?? amountGBP ?? 0).toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-mute mt-2.5 flex items-start gap-1.5 leading-snug">
            <span aria-hidden>🔒</span>
            <span>Full price shown, with no hidden fees. Your payment is held until you confirm receipt and released to the seller only after you confirm the item.</span>
          </p>
        </aside>
        <MoneyLedger variant="buyer" className="mt-4" />
        {!guest && <DeliveryAddressBlock orderId={orderId} />}
        </div>
      </div>
    </Shell>
  );
}

function DemoPayment({ orderId, productId, paymentRef, amountGBP, title, reference, isCart, breakdown, guest }: { orderId?: string; productId?: string; paymentRef: string; amountGBP?: number; title?: string; reference?: string; isCart?: boolean; breakdown?: Breakdown; guest?: boolean }) {
  const navigate = useNavigate();
  const clearCart = useCart((s) => s.clear);
  const remove = useCart((s) => s.remove);
  const [submitting, setSubmitting] = useState(false);
  const total = breakdown?.totalGBP ?? amountGBP ?? 0;

  async function pay() {
    setSubmitting(true);
    try {
      await api.post('/orders/confirm-demo', { paymentRef });
      toast.success('Payment simulated - funds held safely until you collect.');
      if (isCart) clearCart(); else if (productId) remove(productId);
      navigate(`/dashboard/orders${orderId && orderId !== 'cart' ? `?focus=${orderId}` : ''}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not complete the demo payment.');
      setSubmitting(false);
    }
  }

  return (
    <Shell title="Secure checkout">
      <div className="grid md:grid-cols-[1fr_300px] gap-8 items-start">
        <div className="space-y-5">
          <div className="rounded-luxe border border-gold/40 bg-gold/8 p-4 text-sm text-ink-700">
            <span className="font-semibold text-gold-dark">Demo payment mode.</span> Live card payments turn on
            once Stripe keys are added on the server. For now we simulate a successful payment so you can see the
            full order flow - secure hold, locker drop, and collection.
          </div>
          <div className="rounded-luxe border border-warmline bg-white p-5">
            <div className="stamp text-gold-dark text-[9px]">TEST CARD (WHEN STRIPE IS LIVE)</div>
            <p className="text-sm text-mute mt-2 mono">4242 4242 4242 4242 · any future date · any CVC</p>
          </div>
          <button onClick={pay} disabled={submitting} className="btn btn-lg btn-primary w-full">
            {submitting ? 'Processing…' : `Pay${total ? ` £${total.toFixed(2)}` : ''} (demo)`}
          </button>
          <p className="stamp text-mute text-[9px] text-center">
            SIMULATED · FUNDS WOULD BE HELD SAFELY UNTIL YOU CONFIRM COLLECTION
          </p>
        </div>

        <div className="space-y-4">
        <aside className="card p-5">
          <div className="stamp text-gold-dark text-[9px]">ORDER SUMMARY</div>
          {title && <div className="display text-lg mt-2 leading-tight">{title}</div>}
          {reference && <div className="stamp text-mute text-[9px] mt-1">REF {reference}</div>}
          <div className="border-t border-warmline mt-4 pt-4 space-y-2 text-sm">
            {breakdown ? (
              <>
                <Row k="Item(s)" v={`£${breakdown.itemGBP.toFixed(2)}`} />
                {breakdown.member ? (
                  <Row k={breakdown.serviceGBP === 0 ? 'Buyer protection (Member · included)' : 'Buyer protection (Member · 3.5% + 60p)'} v={`£${breakdown.serviceGBP.toFixed(2)}`} />
                ) : (
                  <Row k="Buyer protection fee (4.5% + 70p)" v={`£${breakdown.serviceGBP.toFixed(2)}`} />
                )}
                <Row k="Delivery" v={`£${(breakdown.deliveryGBP + (breakdown.creditGBP ?? 0)).toFixed(2)}`} />
                {breakdown.creditGBP ? <Row k="£4 delivery voucher" v={`−£${breakdown.creditGBP.toFixed(2)}`} /> : null}
              </>
            ) : (
              <Row k="Item" v={amountGBP ? `£${amountGBP.toFixed(2)}` : '-'} />
            )}
          </div>
          <div className="border-t border-warmline mt-4 pt-4 flex items-baseline justify-between">
            <span className="text-sm text-mute">Total</span>
            <span className="display text-2xl">£{total.toFixed(2)}</span>
          </div>
        </aside>
        {!guest && <DeliveryAddressBlock orderId={orderId} />}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-cream min-h-[70vh]">
      <div className="max-w-[1040px] mx-auto px-6 py-12">
        <div className="stamp text-gold-dark text-[9px]">CHECKOUT</div>
        <h1 className="display text-4xl mt-1 mb-8">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-mute">{k}</span>
      <span className="text-ink">{v}</span>
    </div>
  );
}
